import { useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';

import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { token } from '@atlaskit/tokens';

export const JSM_USE_CASE_PROVISIONING_MAX_PENDING_PROGRESS = 0.95;
export const LEGACY_JSM_USE_CASE_PROVISIONING_PROGRESS_DURATION_MS = 120_000;
export const JSM_USE_CASE_PROVISIONING_PROGRESS_DURATION_MS = 105_000;
const PROGRESS_TICK_MS = 500;

export type JsmUseCaseProvisioningProgressStatus = 'neutral' | 'success' | 'error';

type UseJsmUseCaseProvisioningProgressProps = {
	readonly durationMs: number;
	readonly isComplete: boolean;
	readonly isErrored: boolean;
};

type JsmUseCaseProvisioningProgressBarProps = {
	readonly label: string;
	readonly progress: number;
	readonly status?: JsmUseCaseProvisioningProgressStatus;
};

export const useJsmUseCaseProvisioningProgress = ({
	durationMs,
	isComplete,
	isErrored,
}: UseJsmUseCaseProvisioningProgressProps) => {
	const [progress, setProgress] = useState(0);
	const startedAtRef = useRef<number>();

	useEffect(() => {
		if (isComplete) {
			setProgress(1);
			return undefined;
		}

		if (isErrored) {
			return undefined;
		}

		const startedAt = startedAtRef.current ?? Date.now();
		startedAtRef.current = startedAt;

		const intervalId = window.setInterval(() => {
			const elapsedMs = Date.now() - startedAt;
			const nextProgress = Math.min(
				JSM_USE_CASE_PROVISIONING_MAX_PENDING_PROGRESS,
				(elapsedMs / durationMs) * JSM_USE_CASE_PROVISIONING_MAX_PENDING_PROGRESS,
			);
			setProgress(nextProgress);

			if (nextProgress >= JSM_USE_CASE_PROVISIONING_MAX_PENDING_PROGRESS) {
				window.clearInterval(intervalId);
			}
		}, PROGRESS_TICK_MS);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [durationMs, isComplete, isErrored]);

	return progress;
};

export const JsmUseCaseProvisioningProgressBar = ({
	label,
	progress,
	status = 'neutral',
}: JsmUseCaseProvisioningProgressBarProps) => {
	const [showStatusIcon, setShowStatusIcon] = useState(false);

	useEffect(() => {
		setShowStatusIcon(false);

		if (status === 'neutral') {
			return undefined;
		}

		const iconDelayMs = status === 'success' ? 1000 : 0;
		const timeoutId = window.setTimeout(() => {
			setShowStatusIcon(true);
		}, iconDelayMs);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [status]);

	return (
		<div css={progressWrapperStyles}>
			<div
				role="progressbar"
				aria-label={label}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={Math.round(progress * 100)}
				data-progress-status={status}
				css={progressTrackStyles}
			>
				<div
					css={[
						progressIndicatorStyles,
						status === 'error' && progressIndicatorErrorStyles,
						status === 'success' && progressIndicatorSuccessStyles,
					]}
					style={{ width: `${progress * 100}%` }}
				/>
			</div>
			<span
				css={[statusIconWrapperStyles, showStatusIcon && statusIconWrapperVisibleStyles]}
				aria-hidden={!showStatusIcon}
			>
				{status === 'error' && (
					<StatusErrorIcon label="Provisioning failed" color={token('color.icon.danger')} />
				)}
				{status === 'success' && (
					<StatusSuccessIcon label="Provisioning complete" color={token('color.icon.success')} />
				)}
			</span>
		</div>
	);
};

const progressWrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	columnGap: token('space.0'),
	minBlockSize: token('space.300'),
});

const progressTrackStyles = css({
	flexGrow: 1,
	width: '100%',
	height: token('space.075', '6px'),
	overflow: 'hidden',
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.full', '9999px'),
});

const progressIndicatorStyles = css({
	height: '100%',
	backgroundColor: token('color.text'),
	borderRadius: token('radius.full', '9999px'),
	transition: 'width 500ms linear',
});

const progressIndicatorErrorStyles = css({
	backgroundColor: token('color.background.danger.bold'),
});

const progressIndicatorSuccessStyles = css({
	backgroundColor: token('color.background.success.bold'),
});

const statusIconWrapperStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	width: `calc(${token('space.400')} + ${token('space.050')})`,
	height: token('space.300'),
	overflow: 'hidden',
	opacity: 0,
	transition: 'opacity 600ms ease',
});

const statusIconWrapperVisibleStyles = css({
	opacity: 1,
});
